/**
 * IBAN normalleştirme ve doğrulama — ISO 13616 (biçim) + ISO 7064 MOD-97-10 (kontrol basamağı).
 *
 * Sunucudaki `DataScope.Middleware.Finance.Iban` ile AYNI kuralı uygular. İkisi ayrışırsa ekran
 * "geçerli" derken sunucu reddeder (ya da tersi) — kullanıcı neyin yanlış olduğunu anlayamaz.
 * Kural değişirse İKİSİ BİRDEN değişmelidir.
 *
 * ⚠ Doğrulama KOŞULLUDUR: alanın adı "Hesap Numarası / IBAN" — düz hesap numarası girmek geçerli
 * bir kullanımdır. Yalnız IBAN OLMA İDDİASINDAKİ değer (2 harf + 2 rakam ile başlayan) doğrulanır.
 */

/** Boşluk/tire/nokta temizler, büyük harfe çevirir. Saklanacak/gönderilecek biçim budur. */
export function normalizeIban(value) {
  if (!value) return '';
  return String(value).replace(/[\s.-]/g, '').toUpperCase();
}

/** Değer IBAN olma iddiasında mı (ülke kodu + kontrol basamağı biçimi)? */
export function looksLikeIban(value) {
  return /^[A-Z]{2}\d{2}/.test(normalizeIban(value));
}

// Ülke → toplam uzunluk. Tam liste DEĞİL; burada olmayan ülkeye MOD-97 + genel aralık uygulanır.
// Eksik bir ülkeyi peşinen "geçersiz" saymak, geçerli bir havaleyi reddetmek olurdu.
const LENGTH_BY_COUNTRY = {
  TR: 26, DE: 22, GB: 22, NL: 18, FR: 27, IT: 27, ES: 24, BE: 16, AT: 20, CH: 21,
  SE: 24, NO: 15, DK: 18, FI: 18, PL: 28, RO: 24, BG: 22, GR: 27, PT: 25, IE: 22,
  CZ: 24, HU: 28, RU: 33, AZ: 28, SA: 24, AE: 23, QA: 29, KW: 30, IL: 23, CY: 28,
};

/**
 * MOD-97-10: ilk 4 karakter sona alınır, harfler sayıya çevrilir (A=10…Z=35), kalan 1 olmalı.
 * Sayı 34 haneli IBAN'da JS `Number` sınırını KAT KAT aşar → parça parça mod alınır
 * (BigInt yerine; eski tarayıcı/derleyici hedeflerinde de çalışsın).
 */
function mod97(iban) {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const part = /\d/.test(ch) ? ch : String(ch.charCodeAt(0) - 55); // 'A'(65) → 10
    for (const d of part) remainder = (remainder * 10 + Number(d)) % 97;
  }
  return remainder;
}

/** Tam doğrulama. IBAN iddiasında olmayan değer için `false` döner — çağıran önce looksLikeIban'a bakar. */
export function isValidIban(value) {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) return false;
  if (iban.length < 15 || iban.length > 34) return false;

  const expected = LENGTH_BY_COUNTRY[iban.slice(0, 2)];
  if (expected && iban.length !== expected) return false;

  return mod97(iban) === 1;
}

/**
 * Alanın hata mesajı — geçerliyse (ya da IBAN değilse) `null`.
 * Mesaj KULLANICIYA NE YAPACAĞINI söyler: "geçersiz" demek tek başına yardımcı olmuyor.
 */
export function ibanError(value) {
  const iban = normalizeIban(value);
  if (!iban || !looksLikeIban(iban)) return null;   // düz hesap no — doğrulanmaz

  const expected = LENGTH_BY_COUNTRY[iban.slice(0, 2)];
  if (expected && iban.length !== expected) {
    return `${iban.slice(0, 2)} IBAN'ı ${expected} karakter olmalı (girilen: ${iban.length}).`;
  }
  if (!isValidIban(iban)) {
    return 'IBAN kontrol basamağı tutmuyor — hane atlanmış ya da yanlış girilmiş olabilir.';
  }
  return null;
}

/** Gösterim için 4'erli gruplar: `TR33 0006 1005 1978 6457 8413 26`. */
export function formatIban(value) {
  const iban = normalizeIban(value);
  if (!looksLikeIban(iban)) return iban;   // hesap numarasını gruplamayız (yalnız temizleriz)
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * `FieldText`in `format` prop'u için yazarken biçimlendirici.
 *
 * ⚠ AYIRAÇ SİLME TUZAĞI (bkz. utils/phone.js `withSeparatorDelete` — aynı hata sınıfı):
 * değer "TR33 " iken Backspace metnin son karakterini, yani AYIRAÇ olan boşluğu siler.
 * Harf/rakam dizisi DEĞİŞMEZ, biçimlendirici aynı çıktıyı yeniden üretir ve boşluk anında
 * geri gelir → tuş hiç çalışmıyor gibi görünür, alan temizlenemez. Ham metin KISALDIĞI hâlde
 * karakterler aynı kaldıysa "kullanıcı ayıraç sildi" diye yorumlanır ve son KARAKTER düşürülür.
 *
 * (phone.js'teki yardımcı yalnız RAKAM üzerinde çalışır; IBAN harf de içerdiği için
 * alfanümerik sürümü burada.)
 */
export function formatIbanInput(next, prev) {
  const nextStr = String(next ?? '');
  const prevStr = String(prev ?? '');
  const nextRaw = normalizeIban(nextStr);
  const prevRaw = normalizeIban(prevStr);

  if (nextStr.length < prevStr.length && nextRaw === prevRaw) {
    return formatIban(nextRaw.slice(0, -1));
  }
  return formatIban(nextRaw);
}
